/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import auth from "@/auth/authService";
import store from "@/store/store.js"
import i18n from "@/i18n/i18n.js"

import 'firebase/auth'
import { FormPlugin, FormTextareaPlugin } from 'bootstrap-vue';

Vue.use(Router)

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes: [{
            // =============================================================================
            // MAIN LAYOUT ROUTES
            // =============================================================================
            path: '',
            component: () =>
                import ('./layouts/main/Main.vue'),
            children: [
                // =============================================================================
                // 
                // =============================================================================
                {
                    path: '/appDownload',
                    name: 'apk',
                    component: () =>
                        import ('./views/pages/ApkDownload.vue'),
                    meta: {
                        pageTitle: 'AppDownload',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/dashboard',
                    name: 'dashboard',
                    component: () =>
                        import ('./views/pages/dashboard/Dashboard.vue'),
                    meta: {
                        pageTitle: 'Dashboard',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/admin',
                    name: 'admin',
                    component: () =>
                        import ('./views/pages/admin/Admin.vue'),
                    meta: {
                        pageTitle: 'Admin',
                        rule: 'admin',
                        authRequired: true
                    }
                },
                {
                    path: '/user',
                    name: 'user',
                    component: () =>
                        import ('./views/pages/user/User.vue'),
                    meta: {
                        pageTitle: 'User',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/pages/settings',
                    name: 'page-settings',
                    component: () =>
                        import ('./views/pages/settings/Settings.vue'),
                    meta: {
                        pageTitle: 'Profile',
                        rule: 'user'
                    },
                },
                // =============================================================================
                // User File
                // =============================================================================
                {
                    path: '/user-file',
                    name: 'user-file',
                    component: () =>
                        import ('./views/pages/userFile/userFileList/UserFile.vue'),
                    meta: {
                        pageTitle: 'UserFileList',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/user-file/:userId',
                    name: 'user-file-userId',
                    component: () =>
                        import ('./views/pages/userFile/userFileList/UserFile.vue'),
                    meta: {
                        pageTitle: 'UserFileList',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/file-download-history',
                    name: 'file-download-history',
                    component: () =>
                        import ('./views/pages/userFile/fileDownloadHistory/FileDownloadHistory.vue'),
                    meta: {
                        pageTitle: 'FileDownloadHistory',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/file-download-history/:userId',
                    name: 'file-download-history-userId',
                    component: () =>
                        import ('./views/pages/userFile/fileDownloadHistory/FileDownloadHistory.vue'),
                    meta: {
                        pageTitle: 'FileDownloadHistory',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/file-upload-history',
                    name: 'file-upload-history',
                    component: () =>
                        import ('./views/pages/userFile/fileUploadHistory/FileUploadHistory.vue'),
                    meta: {
                        pageTitle: 'FileUploadHistory',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/file-upload-history/:userId',
                    name: 'file-upload-history-userId',
                    component: () =>
                        import ('./views/pages/userFile/fileUploadHistory/FileUploadHistory.vue'),
                    meta: {
                        pageTitle: 'FileUploadHistory',
                        rule: 'user',
                        authRequired: true
                    }
                },


                // =============================================================================
                // Subscription
                // =============================================================================
                {
                    path: '/subscription',
                    name: 'subscription',
                    component: () =>
                        import ('./views/pages/subscription/subscriptionList/Subscription.vue'),
                    meta: {
                        pageTitle: 'SubscriptionList',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/subscription-payment',
                    name: 'subscriptionPayment',
                    component: () =>
                        import ('./views/pages/subscription/subscriptionPayment/SubscriptionPayment.vue'),
                    meta: {
                        pageTitle: 'SubscriptionPaymentHistory',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/subscription-payment/:userId',
                    name: 'subscriptionPayment-userId',
                    component: () =>
                        import ('./views/pages/subscription/subscriptionPayment/SubscriptionPayment.vue'),
                    meta: {
                        pageTitle: 'SubscriptionPaymentHistory',
                        rule: 'user',
                        authRequired: true
                    }
                },

                // =============================================================================
                // Device
                // =============================================================================
                {
                    path: '/device',
                    name: 'device',
                    component: () =>
                        import ('./views/pages/device/Device.vue'),
                    meta: {
                        pageTitle: 'Device',
                        rule: 'user',
                        authRequired: true
                    }
                },

                // =============================================================================
                // Streaming
                // =============================================================================
                {
                    path: '/streaming-play',
                    name: 'StreamingPlayRecord',
                    component: () =>
                        import ('./views/pages/streaming/streamingPlay/StreamingPlay.vue'),
                    meta: {
                        pageTitle: 'StreamingPlayRecord',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/streaming-play/:userId',
                    name: 'StreamingPlayRecord-userId',
                    component: () =>
                        import ('./views/pages/streaming/streamingPlay/StreamingPlay.vue'),
                    meta: {
                        pageTitle: 'StreamingPlayRecord',
                        rule: 'user',
                        authRequired: true
                    }
                },

                // =============================================================================
                // Report
                // =============================================================================
                {
                    path: '/file-stats',
                    name: 'FileStatisticsReport',
                    component: () =>
                        import ('./views/pages/report/fileStatisticsReport/FileStatisticsReport.vue'),
                    meta: {
                        pageTitle: 'FileStatisticsReport',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/streaming-stats',
                    name: 'StreamingStatisticsReport',
                    component: () =>
                        import ('./views/pages/report/streamingStatisticsReport/StreamingStatisticsReport.vue'),
                    meta: {
                        pageTitle: 'StreamingStatisticsReport',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/subscription-stats',
                    name: 'SubscriptionReport',
                    component: () =>
                        import ('./views/pages/report/subscriptionStatisticsReport/SubscriptionStatisticsReport.vue'),
                    meta: {
                        pageTitle: 'SubscriptionStatisticsReport',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/s3-stats',
                    name: 'S3StatisticsReport',
                    component: () =>
                        import ('./views/pages/report/s3UsageReport/S3UsageReport.vue'),
                    meta: {
                        pageTitle: 'S3StatisticsReport',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/wowza-stats',
                    name: 'WowzaStatisticsReport',
                    component: () =>
                        import ('./views/pages/report/wowzaUsageReport/WowzaUsageReport.vue'),
                    meta: {
                        pageTitle: 'WowzaStatisticsReport',
                        rule: 'user',
                        authRequired: true
                    }
                },
                // =============================================================================
                // System Management
                // =============================================================================
                {
                    path: '/server-monitoring-email',
                    name: 'ServerMonitoringEmail',
                    component: () =>
                        import ('./views/pages/system/serverMonitoringEmail/ServerMonitoringEmail.vue'),
                    meta: {
                        pageTitle: 'ServerMonitoringEmail',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/server-monitoring',
                    name: 'ServerMonitoring',
                    component: () =>
                        import ('./views/pages/system/serverMonitoring/ServerMonitoring.vue'),
                    meta: {
                        pageTitle: 'ServerMonitoring',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/version-management',
                    name: 'VersionManagement',
                    component: () =>
                        import ('./views/pages/system/versionManagement/VersionManagement.vue'),
                    meta: {
                        pageTitle: 'VersionManagement',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/notice-management',
                    name: 'NoticeManagement',
                    component: () =>
                        import ('./views/pages/system/noticeManagement/Notice.vue'),
                    meta: {
                        pageTitle: 'NoticeManagement',
                        rule: 'user',
                        authRequired: true
                    }
                },
                {
                    path: '/email-management',
                    name: 'EmailManagement',
                    component: () =>
                        import ('./views/pages/system/emailManagement/Email.vue'),
                    meta: {
                        pageTitle: 'EmailManagement',
                        rule: 'user',
                        authRequired: true
                    }
                },
            ],
        },
        // =============================================================================
        // FULL PAGE LAYOUTS
        // =============================================================================
        {
            path: '',
            component: () =>
                import ('@/layouts/full-page/FullPage.vue'),
            children: [
                // =============================================================================
                // PAGES
                // =============================================================================
                {
                    path: '/pages/login',
                    name: 'page-login',
                    component: () =>
                        import ('@/views/pages/login/Login.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
                {
                    path: '/callback',
                    name: 'auth-callback',
                    component: () =>
                        import ('@/views/Callback.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/reset-password',
                    name: 'page-reset-password',
                    component: () =>
                        import ('@/views/pages/ResetPassword.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
                {
                    path: '/pages/change-password',
                    name: 'page-change-password',
                    component: () =>
                        import ('@/views/pages/ChangePassword.vue'),
                    meta: {
                        rule: 'public'
                    },
                    props: true
                },
                {
                    path: '/pages/lock-screen',
                    name: 'page-lock-screen',
                    component: () =>
                        import ('@/views/pages/LockScreen.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
                {
                    path: '/pages/comingsoon',
                    name: 'page-coming-soon',
                    component: () =>
                        import ('@/views/pages/ComingSoon.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
                {
                    path: '/pages/error-404',
                    name: 'page-error-404',
                    component: () =>
                        import ('@/views/pages/Error404.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
                {
                    path: '/pages/error-500',
                    name: 'page-error-500',
                    component: () =>
                        import ('@/views/pages/Error500.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
                {
                    path: '/pages/not-authorized',
                    name: 'page-not-authorized',
                    component: () =>
                        import ('@/views/pages/NotAuthorized.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
                {
                    path: '/pages/maintenance',
                    name: 'page-maintenance',
                    component: () =>
                        import ('@/views/pages/Maintenance.vue'),
                    meta: {
                        rule: 'public'
                    }
                },
            ]
        },
        // Redirect to 404 page, if no match found
        {
            path: '*',
            redirect: '/pages/error-404'
        }
    ],
})

router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = "none";
    }
})

router.beforeEach(async(to, from, next) => {

    if (from.path === '/' && to.path === '/') {
        let result = await auth.isAuthenticated()
        if (!result) {
            router.push('/pages/login')
        } else {
            router.push('/dashboard')
        }
    }

    // If auth required, check login. If login fails redirect to login page
    if (to.meta.authRequired) {
        let result = await auth.isAuthenticated()

        if (!result) {
            router.push({ path: '/pages/login', query: { to: to.path } }).catch(error => { console.log(error) })
        }
    }

    if (store.state.language && store.state.language !== i18n.locale) {
        i18n.locale = store.state.language;
        next();
    } else if (!store.state.language) {
        store.dispatch('setLanguage', 'en')
            .then(() => {
                i18n.locale = store.state.language;
                next();
            });
    } else {
        next();
    }

    if(Object.keys(from.query).length !== 0 && from.query.to != "/"){
        let result = await auth.isAuthenticated()
        if(result){
            router.push(from.query.to)
        } else {
            router.push('/pages/login')
        }
        
    }

    return next()
        // Specify the current path as the customState parameter, meaning it
        // will be returned to the application after auth
        // auth.login({ target: to.path });

});

export default router